import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Button } from './ui/button-comp'

interface Props {
	children: ReactNode
	fallback?: ReactNode
}

interface State {
	hasError: boolean
	error: Error | null
	errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
	public state: State = {
		hasError: false,
		error: null,
		errorInfo: null,
	}

	public static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error, errorInfo: null }
	}

	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('ErrorBoundary caught an error:', error, errorInfo)
		this.setState({
			error,
			errorInfo,
		})
	}

	private handleReset = () => {
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null,
		})
		// Optionally reload the page
		window.location.reload()
	}

	public render() {
		if (this.state.hasError) {
			// Custom fallback UI
			if (this.props.fallback) {
				return this.props.fallback
			}

			// Default error UI
			return (
				<div className='min-h-screen flex items-center justify-center p-4 bg-background'>
					<div className='max-w-2xl w-full space-y-6'>
						<div className='bg-destructive/15 border border-destructive/20 rounded-lg p-6'>
							<h1 className='text-2xl font-bold text-destructive mb-4'>Algo deu errado</h1>
							<p className='text-destructive/80 mb-4'>Ocorreu um erro inesperado na aplicação.</p>

							{this.state.error && (
								<details className='mb-4'>
									<summary className='cursor-pointer text-sm font-medium text-destructive/70 hover:text-destructive'>Detalhes técnicos (clique para expandir)</summary>
									<div className='mt-2 p-3 bg-black/5 rounded border border-destructive/10'>
										<p className='text-sm font-mono text-destructive/60 break-all'>
											<strong>Erro:</strong> {this.state.error.toString()}
										</p>
										{this.state.errorInfo && <pre className='mt-2 text-xs text-destructive/50 overflow-auto max-h-40'>{this.state.errorInfo.componentStack}</pre>}
									</div>
								</details>
							)}

							<div className='grid grid-rows-2 gap-4 '>
								<Button
									onClick={this.handleReset}
									variant='default'>
									Recarregar Página
								</Button>
								<Button
									onClick={() => (window.location.href = '/')}
									variant='ghost'>
									Voltar ao Início
								</Button>
							</div>
						</div>

						<div className='text-center text-sm text-muted-foreground'>
							<p>Se o problema persistir, entre em contato com o suporte.</p>
						</div>
					</div>
				</div>
			)
		}

		return this.props.children
	}
}
